import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getChallan } from "../../api/auth";
import Logo from "../../assets/logo.png";

const ChallanPage = () => {
  const { challanId } = useParams();
  const [challan, setChallan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);
    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  useEffect(() => {
    const fetchChallan = async () => {
      try {
        const response = await getChallan(challanId);
        if (response.data?.success) {
          const challanData = response.data.data;
          setChallan(challanData);
        } else {
          setError("Challan not found.");
        }
      } catch (err) {
        setError("Failed to load challan. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchChallan();
  }, [challanId]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const isLocal = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
    const backendUrl = isLocal ? "http://localhost:3001" : (process.env.REACT_APP_API_URL || "https://backend.hunarmandpunjab.org.pk");
    const fileUrl = `${backendUrl}/api/onsite/challan/${challanId}/pdf`;
    
    try {
      const response = await fetch(fileUrl);
      if (!response.ok) throw new Error("Failed to fetch PDF");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `challan-${challanId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Blob download failed, fallback to direct open:", err);
      window.location.href = fileUrl;
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger text-center">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const snap = challan?.studentSnapshot || {};
  const isLocal = typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  const backendUrl = isLocal ? "http://localhost:3001" : (process.env.REACT_APP_API_URL || "https://backend.hunarmandpunjab.org.pk");
  const pdfViewUrl = `${backendUrl}/api/onsite/challan/${challanId}/pdf?view=true#toolbar=0&navpanes=0&scrollbar=0&view=FitH`;

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print, header, footer, .header, .footer, #header, #footer { display: none !important; }
          body { margin: 0; }
          .challan-container { box-shadow: none !important; border: 1px solid #000 !important; }
        }
        @keyframes psidPulse {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }
        .psid-pulse-note {
          display: inline-block;
          animation: psidPulse 1.5s ease-in-out infinite;
        }
      `}</style>

      {/* Action Buttons */}
      <div className="no-print d-flex justify-content-center gap-3 my-4">
        <button
          onClick={handlePrint}
          className="btn btn-outline-dark fw-semibold px-4 py-2"
        >
          🖨️ Print Challan
        </button>
        <button
          onClick={handleDownloadPDF}
          className="btn btn-success fw-semibold px-4 py-2"
        >
          ⬇️ Download PDF
        </button>
      </div>

      {/* ── Top Info Section (Header & IDs) ── */}
      <div className="container mb-4" style={{ maxWidth: "1000px" }}>
        <div
          className="challan-container p-4 border rounded shadow-sm bg-white"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          {/* Header */}
          <div className="text-center mb-3">
            <img src={Logo} alt="Hunarmand Punjab" style={{ width: "120px", marginBottom: "12px" }} />
            <h4 className="fw-bold mb-0" style={{ letterSpacing: "1px" }}>HUNARMAND PUNJAB PROGRAM</h4>
            <p className="text-muted mb-0" style={{ fontSize: "13px" }}>Skill Development Initiative | Punjab, Pakistan</p>
            <hr />
            <h5 className="fw-bold text-success">ENTRY TEST FEE CHALLAN</h5>
          </div>

          {/* IDs Section */}
          <div
            className="p-3 mb-3 rounded"
            style={{ background: "#f0fdf4", border: "1px solid #86efac" }}
          >
            <div className="row text-center">
              <div className="col-md-4 mb-2">
                <div style={{ fontSize: "11px", color: "#555", textTransform: "uppercase", letterSpacing: "0.5px" }}>Admit Card ID</div>
                <div className="fw-bold" style={{ fontSize: "14px", color: "#16a34a" }}>{challan.admitCardId}</div>
              </div>
              <div className="col-md-4 mb-2">
                <div style={{ fontSize: "11px", color: "#555", textTransform: "uppercase", letterSpacing: "0.5px" }}>Challan ID</div>
                <div className="fw-bold" style={{ fontSize: "14px", color: "#16a34a" }}>{challan.challanId}</div>
              </div>
              <div className="col-md-4 mb-2">
                <div style={{ fontSize: "11px", color: "#555", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  PSID <span className="psid-pulse-note" style={{ color: "red", fontSize: "10px", textTransform: "none" }}>(Pay via 1Bill / Invoice)</span>
                </div>
                <div className="fw-bold" style={{ fontSize: "14px", color: "#16a34a" }}>{challan.psid}</div>
              </div>
            </div>
          </div>

          {/* Student Details Section - values left aligned */}
          <div className="row g-2" style={{ fontSize: "13px" }}>
            <div className="col-md-6">
              <div className="d-flex align-items-start gap-2">
                <span className="fw-semibold text-muted" style={{ minWidth: "100px", flexShrink: 0 }}>Name:</span>
                <span className="fw-bold text-start">{snap.fullName || "—"}</span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-start gap-2">
                <span className="fw-semibold text-muted" style={{ minWidth: "110px", flexShrink: 0 }}>Father Name:</span>
                <span className="fw-bold text-start">{snap.fatherName || "—"}</span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-start gap-2">
                <span className="fw-semibold text-muted" style={{ minWidth: "100px", flexShrink: 0 }}>Phone:</span>
                <span className="fw-bold text-start">{snap.phone || "—"}</span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex align-items-start gap-2">
                <span className="fw-semibold text-muted" style={{ minWidth: "110px", flexShrink: 0 }}>Email:</span>
                <span className="fw-bold text-start">{snap.email || "—"}</span>
              </div>
            </div>
            {snap.courseApplied && (
              <div className="col-12">
                <div className="d-flex align-items-start gap-2">
                  <span className="fw-semibold text-muted" style={{ minWidth: "100px", flexShrink: 0 }}>Course:</span>
                  <span className="fw-bold text-start">{snap.courseApplied}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {!isMobile && (
        <div
          className="container mb-4 no-print px-0"
          style={{
            maxWidth: "1000px",
            overflow: "hidden",
            borderRadius: "8px",
            backgroundColor: "#fff",
            position: "relative" // Required for the overlay
          }}
        >
          {/* Transparent overlay to block all mouse/touch interactions (prevents zoom in/out & scrolling) */}
          <div
            style={{
              position: "absolute",
              top: 0, left: 0, right: 0, bottom: 0,
              zIndex: 10,
              background: "transparent",
              cursor: "default"
            }}
          />

          <div style={{
            width: "105%",
            marginLeft: "-2.5%",
            marginTop: "-3%",
            marginBottom: "-3%"
          }}>
            <iframe
              className="challan-pdf-frame"
              src={pdfViewUrl}
              title="Challan PDF Preview"
              frameBorder="0"
              scrolling="no"
              style={{
                width: "100%",
                aspectRatio: "842 / 595", // Exact PDF ratio
                border: "0",
                display: "block",
                backgroundColor: "white"
              }}
            />
          </div>
        </div>
      )}

      {/* Challan Document Info */}
      <div className="container mb-5" style={{ maxWidth: "1000px" }}>
        <div
          className="challan-container p-4 border rounded shadow-sm bg-white"
          style={{ fontFamily: "'Courier New', monospace" }}
        >
          {/* Scholarship Info */}
          <div
            className="p-3 mb-3 rounded text-center"
            style={{ background: "#f0fdf4", border: "1px solid #86efac" }}
          >
            <p className="fw-bold mb-1" style={{ fontSize: "14px", color: "#15803d" }}>🎓 Hunarmand Punjab Scholarship Entry Test 2026</p>
            <p className="mb-0" style={{ fontSize: "12px", color: "#374151", lineHeight: "1.6" }}>
              In collaboration with Punjab University, offering industry-focused training in AI, Digital Marketing, Web Development and Ethical Hacking.<br />
              Pay your Entry Test Fee today to unlock scholarship opportunities, laptops, electric bikes and monthly stipends.
            </p>
          </div>

          {/* Payment Instructions + Challan Instructions (side by side) */}
          <div className="row mb-3">
            {/* Payment Instructions */}
            <div className="col-md-6 mb-3 mb-md-0">
              <div
                className="p-3 rounded h-100"
                style={{ background: "#f0f9ff", border: "1px solid #bae6fd" }}
              >
                <p className="fw-semibold mb-1" style={{ fontSize: "13px" }}>💳 Payment Instructions:</p>
                <ul style={{ fontSize: "12px", paddingLeft: "18px", marginBottom: 0 }}>
                  <li>Use the 17-digit PSID: <strong>{challan.psid}</strong> to make your payment.</li>
                  <li>You can pay via Internet Banking, Mobile Wallet (JazzCash, EasyPaisa, etc.), ATM, or any 1-Bill supported bank application.</li>
                  <li>Make sure to pay the entry test fee of <strong>Rs. 500</strong> before the exam date.</li>
                </ul>
              </div>
            </div>

            {/* Challan Instructions */}
            <div className="col-md-6">
              <div
                className="p-3 rounded h-100"
                style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}
              >
                <p className="fw-semibold mb-1" style={{ fontSize: "13px" }}>🏦 Challan Instructions:</p>
                <ul style={{ fontSize: "12px", paddingLeft: "18px", marginBottom: 0 }}>
                  <li>Pay your challan by visiting your nearest Bank of Punjab (BOP) branch.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center" style={{ fontSize: "11px", color: "#888", borderTop: "1px dashed #ccc", paddingTop: "10px" }}>
            <p className="mb-0">Generated: {new Date(challan.generatedAt).toLocaleDateString("en-PK")}</p>
            <p className="mb-0">www.hunarmandpunjab.org.pk |  03-111-133-053</p>
            <p className="mb-0 mt-1" style={{ fontStyle: "italic" }}>
              This is a computer-generated document.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChallanPage;